import { localeConfig } from '@/locale'
import BigNumber from 'bignumber.js'

// 为了避免精度丢失，后端保存数字是实际数值的十万倍
export const PricePrecision = '100000'

export const addPrecision = (price: string | number): string => {
  return new BigNumber(price)
    .times(PricePrecision)
    .decimalPlaces(0, BigNumber.ROUND_FLOOR)
    .toString()
}

export const removePrecision = (price: string | number): string => {
  return new BigNumber(price).div(PricePrecision).toString()
}

export const CURRENCY_SYMBOL = localeConfig.currency.SYMBOL

interface DisplayPriceProps {
  needCurrency?: boolean
  minimumDigits?: number
  useGrouping?: boolean
}

const displayPrice = (price: string | number, options?: DisplayPriceProps) => {
  const {
    currency: { CODE, LOCALES, MIN_FRACTION_DIGITS },
  } = localeConfig

  const { needCurrency, minimumDigits, useGrouping = true } = options || {}

  return parseFloat(price as string).toLocaleString(LOCALES, {
    style: needCurrency ? 'currency' : 'decimal',
    currency: CODE,
    maximumFractionDigits: 5,
    useGrouping: useGrouping,
    minimumFractionDigits: minimumDigits !== undefined ? minimumDigits : MIN_FRACTION_DIGITS,
  })
}

/**
 * 格式化显示金额或者数字
 * @deprecated use formatCurrency or formatNumber instead
 * @param price
 * @param options
 * @returns
 */
export const formatPrice = (price: string | number, options?: DisplayPriceProps) => {
  return displayPrice(removePrecision(`${price}`), options).replace(/\s*/g, '')
}

type FormatCurrencyOption = {
  symbol?: string
  suffix?: boolean
  maxDigits?: number
  minDigits?: number
  useGrouping?: boolean
}

/**
 * 格式化显示金额
 * @param value
 * @param options
 * @returns
 */
export function formatCurrency(value: string | number, options?: FormatCurrencyOption) {
  const {
    currency: { CODE, LOCALES, MIN_FRACTION_DIGITS, MAX_FRACTION_DIGITS, SYMBOL, SUFFIX_SYMBOL },
  } = localeConfig

  const {
    symbol = SYMBOL,
    suffix = SUFFIX_SYMBOL,
    minDigits = MIN_FRACTION_DIGITS,
    maxDigits = MAX_FRACTION_DIGITS,
    useGrouping = true,
  } = options || {}

  const price = parseFloat(removePrecision(`${value}`))
    .toLocaleString(LOCALES, {
      style: 'currency',
      currency: CODE,
      useGrouping,
      maximumFractionDigits: maxDigits,
      minimumFractionDigits: minDigits,
    })
    .replace(SYMBOL, '')
    .replace(/\s*/g, '')

  return suffix ? `${price}${symbol}` : `${symbol}${price}`
}

interface FormatNumberOption {
  minDigits?: number
  maxDigits?: number
  useGrouping?: boolean
}

/**
 * 格式化显示数字
 * @param value
 * @param options
 * @returns
 */
export function formatNumber(value: string | number, options?: FormatNumberOption) {
  const {
    currency: { LOCALES, MIN_FRACTION_DIGITS, MAX_FRACTION_DIGITS },
  } = localeConfig

  const {
    minDigits = MIN_FRACTION_DIGITS,
    maxDigits = MAX_FRACTION_DIGITS,
    useGrouping = true,
  } = options || {}

  return parseFloat(value as string)
    .toLocaleString(LOCALES, {
      style: 'decimal',
      useGrouping,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
    })
    .replace(/\s*/g, '')
}

/**
 * 将已是「展示用」的数值格式化为带货币符号的字符串（不做 PricePrecision / 十万倍 换算）。
 * 适用于表单字段、接口已返回实际金额等场景；与 {@link formatCurrency} 相对，后者面向后端存储的放大整数。
 */
export function formatCurrencyRaw(value: string | number, options?: FormatNumberOption) {
  const {
    currency: { SYMBOL, SUFFIX_SYMBOL },
  } = localeConfig
  const formatted = formatNumber(value, options)
  return SUFFIX_SYMBOL ? `${formatted}${SYMBOL}` : `${SYMBOL}${formatted}`
}

export const assertPrice = (price?: string | number) => !!price && price !== '0'
